import { useEffect } from 'react'
import { useRouter } from 'next/router'

import { useAuth } from '../../services/useAuth'
import useBuyer from './useBuyer'
import { isSuperUser, isSupplier } from '../../lib/supplier'
import useCustomGlobalState from './useCustomGlobalState'
import { useCustomer } from '../BuyerProfile/Account/CustomerProvider'
import { PropertyInclude } from '../../utils/types/customer'
import { selectCustomerIncluded } from '../BuyerProfile/Account/selectors'

const ONBOARDING_MODAL_STATE_KEY = 'buyerOnboardingModalState'

const useBuyerOnboardingModal = () => {
  const [isOnboardingModalOpen, setOnboardingModalOpen] = useCustomGlobalState(
    ONBOARDING_MODAL_STATE_KEY,
    false,
  )

  const { user, isLoadingUser, isImpersonated } = useAuth()
  const { isInvited, getBuyerOnboardingCompletedSteps, isOnboardingDataLoading } = useBuyer()
  const { route } = useRouter()
  const { customerResponse } = useCustomer()

  const openOnboardingModal = () => {
    setOnboardingModalOpen(true)
  }

  const closeOnboardingModal = () => {
    setOnboardingModalOpen(false)
  }

  // Check if user needs onboarding
  useEffect(() => {
    if (user) {
      // Check if industry field is filled
      const hasIndustryFilled = customerResponse
        ? selectCustomerIncluded(customerResponse)?.find(
          (include): include is PropertyInclude => include.type === 'customer.property'
          && include.attributes['customer.property.type'] === 'industry',
        )
        // Fallback true to avoid reopening if customerResponse is null and onboarding is completed
        : true

      // If onboarding is already completed, don't show the modal
      if (hasIndustryFilled && !isInvited) {
        closeOnboardingModal()
        return
      }

      // Don't show during checkout
      if (route === '/checkout/[step]') {
        closeOnboardingModal()
        return
      }

      const { firstname } = user
      const completedSteps = getBuyerOnboardingCompletedSteps()

      const needsOnboarding = ((completedSteps < 2 && firstname)
        && !isSuperUser(user)
        && !isSupplier(user))
        || isInvited

      // For users without firstname, we need special handling
      if (!firstname) {
        // Only show if not on logout page
        if (route !== '/logout') {
          openOnboardingModal()
        }
        return
      }

      // Combined check for when to show the modal
      if (needsOnboarding && route !== '/logout') {
        openOnboardingModal()
      }
    }

    // Close for superusers, suppliers, or impersonated users
    if (isSuperUser(user) || isSupplier(user) || isImpersonated) {
      closeOnboardingModal()
    }
  }, [isLoadingUser, user, isImpersonated, customerResponse, route])

  const shouldShowOnboardingModal = isOnboardingModalOpen && !isOnboardingDataLoading

  return {
    shouldShowOnboardingModal,
    openOnboardingModal,
    closeOnboardingModal,
  }
}

export default useBuyerOnboardingModal
